import { cn } from "@/lib/utils";

type Tone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";

const toneClasses: Record<Tone, string> = {
  neutral: "bg-slate-100 text-slate-700 ring-slate-200",
  brand: "bg-brand-50 text-brand-700 ring-brand-200",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  warning: "bg-amber-50 text-amber-700 ring-amber-200",
  danger: "bg-red-50 text-red-700 ring-red-200",
  info: "bg-sky-50 text-sky-700 ring-sky-200",
};

export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset",
        toneClasses[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

type TeamStatus = "active" | "suspended" | "archived" | string;

function statusTone(status: TeamStatus): Tone {
  switch (status) {
    case "active":
      return "success";
    case "suspended":
      return "danger";
    case "archived":
      return "neutral";
    default:
      return "warning";
  }
}

export function StatusBadge({ status }: { status: TeamStatus }) {
  return <Badge tone={statusTone(status)}>{status}</Badge>;
}

type SubscriptionStatus = "active" | "trialing" | "past_due" | "cancelled" | string;

function subStatusTone(status: SubscriptionStatus): Tone {
  switch (status) {
    case "active":
      return "success";
    case "trialing":
      return "info";
    case "past_due":
      return "warning";
    case "cancelled":
      return "neutral";
    default:
      return "neutral";
  }
}

export function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus }) {
  return <Badge tone={subStatusTone(status)}>{status}</Badge>;
}

type DianProcessStatus = "queued" | "processing" | "completed" | "failed" | string;

function dianTone(status: DianProcessStatus): Tone {
  switch (status) {
    case "queued":
      return "warning";
    case "processing":
      return "info";
    case "completed":
      return "success";
    case "failed":
      return "danger";
    default:
      return "neutral";
  }
}

export function DianStatusBadge({ status }: { status: DianProcessStatus }) {
  return <Badge tone={dianTone(status)}>{status}</Badge>;
}

type CertStatus = "uploaded" | "missing" | "expired" | string;

function certTone(status: CertStatus): Tone {
  switch (status) {
    case "uploaded":
      return "success";
    case "missing":
      return "warning";
    case "expired":
      return "danger";
    default:
      return "neutral";
  }
}

export function CertStatusBadge({ status }: { status: CertStatus }) {
  return <Badge tone={certTone(status)}>{status ?? "—"}</Badge>;
}
